import React, { useState, useEffect, useCallback } from 'react';
import { Users, Plus, User, Bell, FileText, Calendar, Clock, TestTube, ExternalLink, Trash2, Activity, CheckCircle, Play, Wind, BarChart3, TrendingUp, TrendingDown } from 'lucide-react';
import { Card, Button, Spinner } from '../design-system';
import { apiGet, apiPost, apiDelete } from '../../utils/api';
import { useAuth } from '../../contexts/AuthContext';
import { getAuthToken } from '../../services/authService';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import AddPatientModal from './AddPatientModal';

const TherapistHomePage = ({ onNavigateToSection, hasNotificationSubscription }) => {
  const { user, userInfo, toggleDemoMode, authReady } = useAuth();
  const navigate = useNavigate();
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState('active'); // 'active', 'all', or 'recent-activity'
  const [showAddModal, setShowAddModal] = useState(false);
  const [patientActivityData, setPatientActivityData] = useState({}); // patientId -> { isActive, taskCount, appointmentCount }
  const [creatingDemo, setCreatingDemo] = useState(false);
  const [recentActivities, setRecentActivities] = useState([]);
  const [loadingActivities, setLoadingActivities] = useState(false);
  const [analytics, setAnalytics] = useState({
    newClientsThisMonth: 0,
    activeClients: 0,
    droppedClients: 0,
    clientRetention: 0, // Changed from churnRate - this is the inverse
    earlyDropoutRate: 0, // Changed from prematureChurnRate
    averageWeeksActive: 0,
    totalEngagementTime: 0, // in seconds
    tasksCompleted: 0,
    groundingActivitiesCompleted: 0
  });
  const [loadingAnalytics, setLoadingAnalytics] = useState(false);

  // Check if patient has activity in last 30 days
  const checkPatientActivity = useCallback(async (patient) => {
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    
    try {
      // Fetch tasks, appointments, and grounding activities in parallel
      const [tasksResponse, appointmentsResponse, groundingAnalytics] = await Promise.all([
        apiGet(`/.netlify/functions/get-patient-tasks?patientId=${patient.patientId}`, userInfo?.userId).catch(() => ({ tasks: [] })),
        apiGet(`/.netlify/functions/get-bookings?patientId=${patient.patientId}&filter=all`, userInfo?.userId).catch(() => ({ bookings: [] })),
        apiGet(`/.netlify/functions/get-grounding-analytics?patientId=${patient.patientId}&timeRange=month`, userInfo?.userId).catch(() => ({ analytics: null }))
      ]);

      const tasks = tasksResponse.tasks || [];
      const appointments = appointmentsResponse.bookings || [];
      const groundingData = groundingAnalytics?.analytics || null;

      // Check if any activity in last 30 days
      const hasRecentTask = tasks.some(task => {
        const taskDate = task.completedAt || task.startedAt || task.createdAt;
        if (!taskDate) return false;
        const date = new Date(taskDate);
        return date >= thirtyDaysAgo;
      });

      const hasRecentAppointment = appointments.some(apt => {
        if (!apt.date) return false;
        const date = new Date(apt.date);
        return date >= thirtyDaysAgo;
      });

      const hasRecentGrounding = groundingData?.rawSessions?.some(session => {
        const sessionDate = session.completedAt || session.startedAt || session.createdAt;
        if (!sessionDate) return false;
        const date = new Date(sessionDate);
        return date >= thirtyDaysAgo;
      }) || false;

      const isActive = hasRecentTask || hasRecentAppointment || hasRecentGrounding;
      const taskCount = tasks.filter(t => t.status === 'pending' || t.status === 'started').length;
      const appointmentCount = appointments.filter(a => {
        if (!a.date) return false;
        return new Date(a.date) >= new Date();
      }).length;

      return {
        isActive,
        taskCount,
        appointmentCount
      };
    } catch (error) {
      console.error(`Error checking activity for patient ${patient.patientId}:`, error);
      return { isActive: false, taskCount: 0, appointmentCount: 0 };
    }
  }, [userInfo?.userId]);

  // Fetch patients and their activity data
  const fetchPatients = useCallback(async () => {
    try {
      setLoading(true);
      const data = await apiGet('/.netlify/functions/get-patients', userInfo?.userId);
      const fetchedPatients = data.patients || [];
      setPatients(fetchedPatients);

      // Check activity for all patients in parallel
      const activityPromises = fetchedPatients.map(patient => 
        checkPatientActivity(patient).then(activity => ({ patientId: patient.patientId, ...activity }))
      );
      
      const activityResults = await Promise.all(activityPromises);
      const activityMap = {};
      activityResults.forEach(result => {
        activityMap[result.patientId] = {
          isActive: result.isActive,
          taskCount: result.taskCount,
          appointmentCount: result.appointmentCount
        };
      });
      setPatientActivityData(activityMap);
    } catch (error) {
      console.error('Error fetching patients:', error);
      toast.error('Failed to load patients');
    } finally {
      setLoading(false);
    }
  }, [userInfo?.userId, checkPatientActivity]);

  useEffect(() => {
    // Wait for auth to be ready and user to be set before making API calls
    if (!authReady || !user) {
      return;
    }
    
    // Check if we can get a token - this handles React state batching
    const token = getAuthToken();
    if (!token) {
      // Token not available yet - this can happen if user state hasn't fully updated
      // The effect will re-run when user changes, so we'll try again
      return;
    }
    
    fetchPatients();
  }, [fetchPatients, authReady, user]);

  // Fetch recent activities from all patients
  const fetchRecentActivities = useCallback(async () => {
    if (patients.length === 0) return;
    
    try {
      setLoadingActivities(true);
      const activities = [];
      const sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);

      // Fetch activities from all patients in parallel
      const activityPromises = patients.map(async (patient) => {
        try {
          const [tasksResponse, groundingAnalytics] = await Promise.all([
            apiGet(`/.netlify/functions/get-patient-tasks?patientId=${patient.patientId}`, userInfo?.userId).catch(() => ({ tasks: [] })),
            apiGet(`/.netlify/functions/get-grounding-analytics?patientId=${patient.patientId}&timeRange=week`, userInfo?.userId).catch(() => ({ analytics: null }))
          ]);

          const tasks = tasksResponse.tasks || [];
          const groundingData = groundingAnalytics?.analytics || null;

          // Add completed tasks
          tasks.forEach(task => {
            if (task.completedAt) {
              const date = new Date(task.completedAt);
              if (date >= sevenDaysAgo) {
                activities.push({
                  id: `task-completed-${task.taskId}`,
                  type: 'task-completed',
                  patientId: patient.patientId,
                  patientName: patient.name,
                  title: task.title,
                  timestamp: date.getTime(),
                  date: date,
                  data: task
                });
              }
            }
          });

          // Add started tasks
          tasks.forEach(task => {
            if (task.startedAt && !task.completedAt) {
              const date = new Date(task.startedAt);
              if (date >= sevenDaysAgo) {
                activities.push({
                  id: `task-started-${task.taskId}`,
                  type: 'task-started',
                  patientId: patient.patientId,
                  patientName: patient.name,
                  title: task.title,
                  timestamp: date.getTime(),
                  date: date,
                  data: task
                });
              }
            }
          });

          // Add grounding sessions from analytics
          if (groundingData && groundingData.rawSessions) {
            groundingData.rawSessions.forEach(session => {
              // Use completedAt if available, otherwise use createdAt
              const date = session.completedAt ? new Date(session.completedAt) : new Date(session.createdAt);
              if (date >= sevenDaysAgo) {
                activities.push({
                  id: `grounding-${session.sessionId}`,
                  type: session.status === 'completed' ? 'grounding' : 'grounding-started',
                  patientId: patient.patientId,
                  patientName: patient.name,
                  title: session.status === 'completed' ? 'Grounding exercise completed' : 'Grounding exercise started',
                  timestamp: date.getTime(),
                  date: date,
                  data: session
                });
              }
            });
          }
        } catch (error) {
          console.error(`Error fetching activities for patient ${patient.patientId}:`, error);
        }
      });

      await Promise.all(activityPromises);

      // Sort by timestamp (most recent first)
      activities.sort((a, b) => b.timestamp - a.timestamp);
      
      // Limit to most recent 50 activities
      setRecentActivities(activities.slice(0, 50));
    } catch (error) {
      console.error('Error fetching recent activities:', error);
      toast.error('Failed to load recent activities');
    } finally {
      setLoadingActivities(false);
    }
  }, [patients, userInfo?.userId]);

  // Fetch analytics data
  const fetchAnalytics = useCallback(async () => {
    if (patients.length === 0) return;

    try {
      setLoadingAnalytics(true);
      
      // Calculate date range for current month
      const now = new Date();
      const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

      // 1. Count new clients this month
      const newClientsThisMonth = patients.filter(patient => {
        const createdAt = new Date(patient.createdAt);
        return createdAt >= startOfMonth;
      }).length;

      // 2. Calculate active clients, dropped clients, churn rates, and average weeks active
      let activeClients = 0;
      let churnedPatients = 0;
      let prematureChurnedPatients = 0;
      let totalPatientsWithHistory = 0;
      let totalWeeksActive = 0;
      let patientsWithActivityData = 0;

      // 3. Calculate total engagement time and activity counts
      let totalEngagementSeconds = 0;
      let tasksCompletedThisMonth = 0;
      let groundingActivitiesCompletedThisMonth = 0;

      // Process each patient to check activity and calculate engagement
      const analyticsPromises = patients.map(async (patient) => {
        try {
          const [tasksResponse, groundingAnalytics] = await Promise.all([
            apiGet(`/.netlify/functions/get-patient-tasks?patientId=${patient.patientId}`, userInfo?.userId).catch(() => ({ tasks: [] })),
            apiGet(`/.netlify/functions/get-grounding-analytics?patientId=${patient.patientId}&timeRange=month`, userInfo?.userId).catch(() => ({ analytics: null }))
          ]);

          const tasks = tasksResponse.tasks || [];
          const groundingData = groundingAnalytics?.analytics || null;

          // Collect all activity dates for this patient
          const allActivityDates = [];
          
          tasks.forEach(task => {
            const dates = [task.completedAt, task.startedAt, task.createdAt].filter(Boolean);
            dates.forEach(date => allActivityDates.push(new Date(date)));
          });
          
          if (groundingData?.rawSessions) {
            groundingData.rawSessions.forEach(session => {
              const dates = [session.completedAt, session.startedAt, session.createdAt].filter(Boolean);
              dates.forEach(date => allActivityDates.push(new Date(date)));
            });
          }

          // Calculate first and last activity dates
          let firstActivityDate = null;
          let lastActivityDate = null;
          
          if (allActivityDates.length > 0) {
            firstActivityDate = new Date(Math.min(...allActivityDates.map(d => d.getTime())));
            lastActivityDate = new Date(Math.max(...allActivityDates.map(d => d.getTime())));
            
            // Calculate weeks active for this patient
            const weeksActive = (lastActivityDate - firstActivityDate) / (1000 * 60 * 60 * 24 * 7);
            if (weeksActive > 0) {
              totalWeeksActive += weeksActive;
              patientsWithActivityData++;
            }
          }

          // Check if patient has any historical activity (before 30 days ago)
          const hasHistoricalActivity = tasks.some(task => {
            const taskDate = task.completedAt || task.startedAt || task.createdAt;
            if (!taskDate) return false;
            return new Date(taskDate) < thirtyDaysAgo;
          }) || (groundingData?.rawSessions?.some(session => {
            const sessionDate = session.completedAt || session.startedAt || session.createdAt;
            if (!sessionDate) return false;
            return new Date(sessionDate) < thirtyDaysAgo;
          }) || false);

          // Check if patient has activity in last 30 days
          const hasRecentActivity = tasks.some(task => {
            const taskDate = task.completedAt || task.startedAt || task.createdAt;
            if (!taskDate) return false;
            const date = new Date(taskDate);
            return date >= thirtyDaysAgo;
          }) || (groundingData?.rawSessions?.some(session => {
            const sessionDate = session.completedAt || session.startedAt || session.createdAt;
            if (!sessionDate) return false;
            return new Date(sessionDate) >= thirtyDaysAgo;
          }) || false);

          if (hasHistoricalActivity) {
            totalPatientsWithHistory++;

            if (!hasRecentActivity) {
              churnedPatients++;
              
              // Check for premature churn (stopped before 3 weeks)
              if (firstActivityDate && lastActivityDate) {
                const daysActive = (lastActivityDate - firstActivityDate) / (1000 * 60 * 60 * 24);
                if (daysActive < 21) { // Less than 3 weeks (21 days)
                  prematureChurnedPatients++;
                }
              }
            } else {
              activeClients++;
            }
          } else {
            // Patient is new (no historical activity), check if they have recent activity
            if (hasRecentActivity) {
              activeClients++;
            }
          }

          // Calculate engagement time and activity counts for this month
          // Grounding sessions: use duration field (already in seconds)
          if (groundingData?.rawSessions) {
            groundingData.rawSessions.forEach(session => {
              const sessionDate = session.completedAt || session.startedAt || session.createdAt;
              if (sessionDate && new Date(sessionDate) >= startOfMonth) {
                // Count completed grounding activities
                if (session.status === 'completed') {
                  groundingActivitiesCompletedThisMonth++;
                }
                
                // Add to engagement time
                if (session.duration) {
                  totalEngagementSeconds += session.duration;
                } else if (session.completedAt && session.startedAt) {
                  // Fallback: calculate duration if not stored
                  const duration = Math.floor((new Date(session.completedAt) - new Date(session.startedAt)) / 1000);
                  totalEngagementSeconds += duration;
                }
              }
            });
          }

          // Tasks: calculate completedAt - startedAt for completed tasks
          tasks.forEach(task => {
            if (task.completedAt) {
              const completedDate = new Date(task.completedAt);
              if (completedDate >= startOfMonth && task.status === 'completed') {
                // Count completed tasks
                tasksCompletedThisMonth++;
                
                // Add to engagement time if startedAt exists
                if (task.startedAt) {
                  const duration = Math.floor((new Date(task.completedAt) - new Date(task.startedAt)) / 1000);
                  totalEngagementSeconds += duration;
                }
              }
            }
          });

          return { churned: !hasRecentActivity && hasHistoricalActivity };
        } catch (error) {
          console.error(`Error calculating analytics for patient ${patient.patientId}:`, error);
          return null;
        }
      });

      await Promise.all(analyticsPromises);

      // Calculate retention and dropout rates
      // Client Retention = percentage of patients with history who are still active
      const clientRetention = totalPatientsWithHistory > 0 
        ? ((totalPatientsWithHistory - churnedPatients) / totalPatientsWithHistory) * 100 
        : 0;
      
      // Early Dropout Rate = percentage of patients who dropped before 3 weeks
      const earlyDropoutRate = totalPatientsWithHistory > 0
        ? (prematureChurnedPatients / totalPatientsWithHistory) * 100
        : 0;

      // Calculate average weeks active
      const averageWeeksActive = patientsWithActivityData > 0
        ? totalWeeksActive / patientsWithActivityData
        : 0;

      setAnalytics({
        newClientsThisMonth,
        activeClients,
        droppedClients: churnedPatients,
        clientRetention: Math.round(clientRetention * 10) / 10, // Round to 1 decimal place
        earlyDropoutRate: Math.round(earlyDropoutRate * 10) / 10,
        averageWeeksActive: Math.round(averageWeeksActive * 10) / 10,
        totalEngagementTime: totalEngagementSeconds,
        tasksCompleted: tasksCompletedThisMonth,
        groundingActivitiesCompleted: groundingActivitiesCompletedThisMonth
      });
    } catch (error) {
      console.error('Error fetching analytics:', error);
      toast.error('Failed to load analytics');
    } finally {
      setLoadingAnalytics(false);
    }
  }, [patients, userInfo?.userId]);

  // Fetch activities and analytics when patients are loaded
  useEffect(() => {
    if (patients.length > 0) {
      fetchRecentActivities();
      fetchAnalytics();
    }
  }, [patients.length, fetchRecentActivities, fetchAnalytics]);

  // Filter patients based on active filter
  const filteredPatients = patients.filter(patient => {
    if (activeFilter === 'active') {
      // Check if patient is new (created within last 30 days)
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      const patientCreatedAt = patient.createdAt ? new Date(patient.createdAt) : null;
      const isNewPatient = patientCreatedAt && patientCreatedAt >= thirtyDaysAgo;
      
      // Show new patients even if they don't have activity yet
      if (isNewPatient) {
        return true;
      }
      
      // For older patients, check if they have activity
      const activity = patientActivityData[patient.patientId];
      return activity?.isActive === true;
    }
    return true; // 'all' shows everyone
  });

  const handlePatientSelect = (patient) => {
    navigate(`/dashboard/therapist/patient/${patient.patientId}`);
  };

  // Check if demo patient exists
  const hasDemoPatient = patients.some(patient => 
    patient.isDemo === true || 
    (patient.email && patient.email.includes('@demo.theraengage.com'))
  );

  const createDemoPatient = async () => {
    try {
      setCreatingDemo(true);
      await apiPost('/.netlify/functions/create-demo-patient', {}, null, userInfo?.userId);
      toast.success('Demo patient created successfully!');
      fetchPatients(); // Refresh the list
    } catch (error) {
      console.error('Error creating demo patient:', error);
      toast.error(error.message || 'Failed to create demo patient');
    } finally {
      setCreatingDemo(false);
    }
  };

  const deletePatient = async (patientId, patientName) => {
    if (!window.confirm(`Are you sure you want to delete ${patientName || 'this patient'}? This action cannot be undone.`)) {
      return;
    }

    try {
      await apiDelete('/.netlify/functions/delete-patient', { patientId }, userInfo?.userId);
      toast.success('Patient deleted successfully');
      fetchPatients(); // Refresh the list
    } catch (error) {
      console.error('Error deleting patient:', error);
      toast.error('Failed to delete patient');
    }
  };

  return (
    <div className="space-y-6">
      {/* Header Section */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Welcome back, {(() => {
            // Try to get name from userInfo (from database) first
            if (userInfo?.name) return userInfo.name;
            
            // Fall back to Netlify metadata
            const fullName = userInfo?.user_metadata?.full_name || userInfo?.user_metadata?.name;
            if (fullName) return fullName;
            
            // If no name, format email nicely
            const email = user?.email;
            if (email) {
              const emailName = email.split('@')[0];
              return emailName.charAt(0).toUpperCase() + emailName.slice(1);
            }
            
            return 'Therapist';
          })()}</h1>
        </div>
        <div className="flex items-center gap-3 flex-wrap ml-auto">
          {!loading && !hasDemoPatient && (
            <Button
              onClick={createDemoPatient}
              disabled={creatingDemo}
              variant="secondary"
              size="medium"
            >
              <TestTube className="h-4 w-4 mr-2" />
              {creatingDemo ? 'Creating...' : 'Demo Patient'}
            </Button>
          )}
        </div>
      </div>

      {/* Analytics Tile */}
      <Card variant="default" padding="medium">
        <div className="flex items-center gap-2 mb-3">
          <BarChart3 className="h-4 w-4 text-indigo-600" />
          <h2 className="text-base font-semibold text-gray-900">Monthly Analytics</h2>
        </div>

        {loadingAnalytics ? (
          <div className="text-center py-4">
            <Spinner size="small" className="mx-auto" />
            <p className="text-gray-500 mt-2 text-xs">Loading analytics...</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {/* Widget 1: Client Overview */}
            <div className="bg-indigo-50 rounded-lg p-3 border border-indigo-200">
              <div className="flex items-center gap-1.5 mb-2">
                <Users className="h-3.5 w-3.5 text-indigo-600" />
                <span className="text-xs font-semibold text-gray-900">Client Overview</span>
              </div>
              <div className="space-y-1.5">
                <div className="flex items-baseline justify-between">
                  <span className="text-[10px] text-gray-600">New Clients:</span>
                  <span className="text-sm font-bold text-indigo-900">{analytics.newClientsThisMonth}</span>
                </div>
                <div className="flex items-baseline justify-between">
                  <span className="text-[10px] text-gray-600">Active Clients:</span>
                  <span className="text-sm font-bold text-green-700">{analytics.activeClients}</span>
                </div>
                <div className="flex items-baseline justify-between">
                  <span className="text-[10px] text-gray-600">Inactive:</span>
                  <span className="text-sm font-bold text-red-700">{analytics.droppedClients}</span>
                </div>
                <div className="flex items-baseline justify-between">
                  <span className="text-[10px] text-gray-600">Avg Engagement:</span>
                  <span className="text-sm font-bold text-indigo-900">{analytics.averageWeeksActive}w</span>
                </div>
              </div>
            </div>

            {/* Widget 2: Retention Metrics */}
            <div className="bg-orange-50 rounded-lg p-3 border border-orange-200">
              <div className="flex items-center gap-1.5 mb-2">
                <TrendingUp className="h-3.5 w-3.5 text-orange-600" />
                <span className="text-xs font-semibold text-gray-900">Retention Metrics</span>
              </div>
              <div className="space-y-1.5">
                <div className="flex items-baseline justify-between">
                  <span className="text-[10px] text-gray-600">Client Retention:</span>
                  <span className={`text-sm font-bold ${
                    analytics.clientRetention >= 90 
                      ? 'text-green-700'
                      : analytics.clientRetention >= 70
                      ? 'text-yellow-700'
                      : 'text-red-700'
                  }`}>
                    {analytics.clientRetention}%
                  </span>
                </div>
                <div className="flex items-baseline justify-between">
                  <span className="text-[10px] text-gray-600">Early Dropout:</span>
                  <span className={`text-sm font-bold ${
                    analytics.earlyDropoutRate === 0 
                      ? 'text-green-700'
                      : analytics.earlyDropoutRate < 5
                      ? 'text-yellow-700'
                      : 'text-red-700'
                  }`}>
                    {analytics.earlyDropoutRate}%
                  </span>
                </div>
              </div>
            </div>

            {/* Widget 3: Engagement Metrics */}
            <div className="bg-green-50 rounded-lg p-3 border border-green-200">
              <div className="flex items-center gap-1.5 mb-2">
                <Activity className="h-3.5 w-3.5 text-green-600" />
                <span className="text-xs font-semibold text-gray-900">Engagement</span>
              </div>
              <div className="space-y-1.5">
                <div className="flex items-baseline justify-between">
                  <span className="text-[10px] text-gray-600">Total Client Activity Time:</span>
                  <span className="text-sm font-bold text-green-900">
                    {(() => {
                      const hours = Math.floor(analytics.totalEngagementTime / 3600);
                      const minutes = Math.floor((analytics.totalEngagementTime % 3600) / 60);
                      if (hours > 0) {
                        return `${hours}h ${minutes}m`;
                      }
                      return `${minutes}m`;
                    })()}
                  </span>
                </div>
                <div className="flex items-baseline justify-between">
                  <span className="text-[10px] text-gray-600">Assigned Tasks Completed:</span>
                  <span className="text-sm font-bold text-green-900">{analytics.tasksCompleted}</span>
                </div>
                <div className="flex items-baseline justify-between">
                  <span className="text-[10px] text-gray-600">Grounding Exercises Done:</span>
                  <span className="text-sm font-bold text-green-900">{analytics.groundingActivitiesCompleted}</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </Card>

      {/* Two Tile Layout: Clients and Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-stretch">
        {/* Clients Tile */}
        <Card variant="default" padding="large" className="flex flex-col h-full">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Users className="h-5 w-5 text-indigo-600" />
              <h2 className="text-lg font-semibold text-gray-900">Clients</h2>
            </div>
            <div className="flex items-center gap-2">
              <div className="flex space-x-1 border border-gray-200 rounded-lg p-1">
          <button
            onClick={() => setActiveFilter('active')}
                  className={`px-3 py-1 text-xs font-medium transition-colors rounded ${
              activeFilter === 'active'
                      ? 'bg-indigo-600 text-white'
                      : 'text-gray-600 hover:text-gray-900'
            }`}
          >
                  Active
          </button>
          <button
            onClick={() => setActiveFilter('all')}
                  className={`px-3 py-1 text-xs font-medium transition-colors rounded ${
              activeFilter === 'all'
                      ? 'bg-indigo-600 text-white'
                      : 'text-gray-600 hover:text-gray-900'
            }`}
          >
                  All
          </button>
        </div>
        <Button
          onClick={() => setShowAddModal(true)}
          variant="primary"
          size="small"
        >
          <Plus className="h-4 w-4" />
        </Button>
            </div>
      </div>

      {loading ? (
        <div className="text-center py-12">
          <Spinner size="large" className="mx-auto" />
              <p className="text-gray-500 mt-4 text-sm">Loading clients...</p>
        </div>
      ) : filteredPatients.length === 0 ? (
            <div className="text-center py-8">
              <Users className="h-10 w-10 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-500 text-sm font-medium mb-1">
              {activeFilter === 'active' 
                ? 'No active clients' 
                : 'No patients added yet'}
            </p>
              <p className="text-xs text-gray-400 mb-4">
              {activeFilter === 'active'
                ? 'Clients with activity in the last 30 days will appear here'
                : 'Add your first patient to get started'}
            </p>
            {activeFilter === 'active' && (
              <Button
                onClick={() => setActiveFilter('all')}
                variant="outline"
                  size="small"
              >
                View All Clients
              </Button>
            )}
            {activeFilter === 'all' && (
              <Button
                onClick={() => setShowAddModal(true)}
                variant="primary"
                  size="small"
              >
                  <Plus className="h-4 w-4 mr-1" />
                Add Patient
              </Button>
            )}
          </div>
      ) : (
            <div className="space-y-3 overflow-y-auto max-h-[400px]">
          {filteredPatients.map((patient) => {
            const activity = patientActivityData[patient.patientId] || { taskCount: 0, appointmentCount: 0 };
            return (
              <Card key={patient._id} variant="default" padding="medium" className="hover:shadow-md transition-shadow relative">
                {/* Delete Button - Top Right Corner */}
                <div className="absolute top-1 right-1 z-10">
                  <Button
                    onClick={(e) => {
                      e.stopPropagation();
                      deletePatient(patient._id, patient.name);
                    }}
                    variant="ghost"
                    size="small"
                    className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-full"
                    title="Delete patient"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>

                    <div className="flex items-start space-x-3 pr-8">
                    <div className="h-10 w-10 rounded-full flex items-center justify-center flex-shrink-0" style={{backgroundColor: 'rgba(44, 166, 164, 0.1)'}}>
                      <User className="h-5 w-5" style={{color: 'var(--color-primary)'}} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-900 truncate mb-1">
                        {patient.name || 'Unnamed Patient'}
                      </p>
                        <div className="flex items-center gap-3 text-xs text-gray-600">
                        <div className="flex items-center gap-1">
                            <FileText className="h-3 w-3" />
                          <span>{activity.taskCount} tasks</span>
                        </div>
                        <div className="flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                          <span>{activity.appointmentCount} upcoming</span>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-2 mt-3">
                    <Button
                      onClick={() => handlePatientSelect(patient)}
                      variant="primary"
                        size="small"
                      className="flex-1"
                    >
                      View Details
                    </Button>
                    {patient.isDemo && (
                      <Button
                        onClick={() => {
                          toggleDemoMode(true);
                          setTimeout(() => {
                            navigate('/dashboard/patient');
                          }, 0);
                        }}
                        variant="secondary"
                          size="small"
                        className="flex-1"
                      >
                          <ExternalLink className="h-3 w-3 mr-1" />
                          Portal
                      </Button>
                    )}
                    </div>
                  </Card>
                );
              })}
            </div>
          )}
        </Card>

        {/* Recent Activity Tile */}
        <Card variant="default" padding="large" className="flex flex-col h-full">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Activity className="h-5 w-5 text-indigo-600" />
              <h2 className="text-lg font-semibold text-gray-900">Recent Activity</h2>
            </div>
            <span className="text-xs text-gray-500">Last 7 days</span>
          </div>

          {loadingActivities ? (
            <div className="text-center py-12">
              <Spinner size="large" className="mx-auto" />
              <p className="text-gray-500 mt-4 text-sm">Loading activities...</p>
            </div>
          ) : recentActivities.length === 0 ? (
            <div className="text-center py-8">
              <Activity className="h-10 w-10 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-500 text-sm font-medium mb-1">No recent activity</p>
              <p className="text-xs text-gray-400">
                Activities from the last 7 days will appear here
              </p>
            </div>
          ) : (
            <div className="space-y-2 overflow-y-auto max-h-[400px]">
              {recentActivities.map((activity) => {
                const getActivityIcon = () => {
                  switch (activity.type) {
                    case 'task-completed':
                      return <CheckCircle className="h-4 w-4 text-green-600" />;
                    case 'task-started':
                      return <Play className="h-4 w-4 text-blue-600" />;
                    case 'grounding':
                      return <Wind className="h-4 w-4 text-purple-600" />;
                    case 'grounding-started':
                      return <Wind className="h-4 w-4 text-purple-500" />;
                    default:
                      return <Activity className="h-4 w-4 text-gray-600" />;
                  }
                };

                const getActivityColor = () => {
                  switch (activity.type) {
                    case 'task-completed':
                      return 'bg-green-50 border-green-200';
                    case 'task-started':
                      return 'bg-blue-50 border-blue-200';
                    case 'grounding':
                      return 'bg-purple-50 border-purple-200';
                    case 'grounding-started':
                      return 'bg-purple-50 border-purple-200';
                    default:
                      return 'bg-gray-50 border-gray-200';
                  }
                };

                const getActivityLabel = () => {
                  switch (activity.type) {
                    case 'task-completed':
                      return 'Completed task';
                    case 'task-started':
                      return 'Started task';
                    case 'grounding':
                      return 'Grounding exercise';
                    case 'grounding-started':
                      return 'Grounding exercise';
                    default:
                      return 'Activity';
                  }
                };

                const formatTime = (date) => {
                  const now = new Date();
                  const diffMs = now.getTime() - date.getTime();
                  const diffMins = Math.floor(diffMs / 60000);
                  const diffHours = Math.floor(diffMs / 3600000);
                  const diffDays = Math.floor(diffMs / 86400000);

                  if (diffMins < 1) return 'Just now';
                  if (diffMins < 60) return `${diffMins}m ago`;
                  if (diffHours < 24) return `${diffHours}h ago`;
                  if (diffDays === 1) return 'Yesterday';
                  if (diffDays < 7) return `${diffDays}d ago`;
                  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                };

                return (
                  <Card
                    key={activity.id}
                    variant="default"
                    padding="small"
                    className={`hover:shadow-md transition-shadow cursor-pointer ${getActivityColor()}`}
                    onClick={() => handlePatientSelect({ patientId: activity.patientId })}
                  >
                    <div className="flex items-start space-x-2">
                      <div className="flex-shrink-0 mt-0.5">
                        {getActivityIcon()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-medium text-gray-900 truncate">
                              {activity.title}
                            </p>
                            <p className="text-xs text-gray-600 mt-0.5">
                              {getActivityLabel()} by <span className="font-medium">{activity.patientName}</span>
                            </p>
                          </div>
                          <div className="flex-shrink-0 text-xs text-gray-500">
                            {formatTime(activity.date)}
                          </div>
                        </div>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}
        </Card>
      </div>

      {/* Add Patient Modal */}
      <AddPatientModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onPatientAdded={fetchPatients}
      />
    </div>
  );
};

export default TherapistHomePage;
